import { useCallback, useEffect, useMemo, useRef } from 'react';

import { BetActionErrorResponseDataDto } from 'api/common.generated';
import BetslipCouponCard from 'features/coupon/component/BetslipCouponCard';
import { systemBetGroupNameArray } from 'features/coupon/utility';
import { useTranslation } from 'react-i18next';
import BetCalculator from 'shared/components/calculators/BetCalculator';
import { BETSType, PlaceBet } from 'shared/interface/index';
import { getNumOfCombinations } from 'shared/util/utility';
import ChangeSystemCombination from '../ChangeSystemCombination';
import ErrorCodes from '../errorCode';
import './styles.scss';

interface SystemBetProps {
	outcomes: PlaceBet[];
	blocks: PlaceBet[][];
	removeBet: (id: string) => void;
	betStatics: any;
	onChangeStackValue: (value: number) => void;
	handelSelectedBet: (value: boolean, id: string) => void;
	handleOddsChangeOption: (value: string) => void;
	selectedCombination: string;
	handleSelectedCombination: (value: string) => void;
	onClickPlaceBet: () => void;
	stackValue: number;
	changeNavigationView: () => void;
	errorCode: number[];
	loading: boolean;
	onCloseError: (error: number, indexOfErrorDetail: number) => void;
	betLoading: boolean;
	betType: string;
	approvedSlip: boolean;
	betActionErrors: BetActionErrorResponseDataDto[];
	warnings: string[];
	completedEvents: string[];
	handleGroupChange: (outcomeId: string, newGroupIndex: number) => void;
	isDesktop: boolean;
	isCal: boolean;
	handleIsCal: (value: boolean) => void;
	handleIsRestoringBets: (isRestoring: boolean) => void;
	totalOdds?: number;
	setStakeMethod: (stake: string) => void;
	bets: BETSType;
}

const SystemBet = (props: SystemBetProps) => {
	const { onChangeStackValue, outcomes, blocks, handleIsCal, isCal, setStakeMethod } = props;
	const { t } = useTranslation();

	const couponContentRef = useRef<HTMLHeadingElement>(null);
	const allBets = useMemo(() => [...blocks, ...outcomes], [blocks, outcomes]);

	const numOfSlipsWithGroups = props.outcomes.filter((item) => !item.banker).length + props.blocks.length;
	const numOfCombinations = getNumOfCombinations(numOfSlipsWithGroups, Number(props.selectedCombination));

	const stakeValue = props.stackValue * numOfCombinations;
	const numOfBlocks = props.blocks?.length;
	const bankerCount = props.outcomes.filter((item) => item.banker).length;

	const bankerBets = props.outcomes?.filter((outcome) => outcome.banker);
	const notBankerBets = props.outcomes?.filter((outcome) => !outcome.banker);

	const onCancelCal = useCallback(() => {
		handleIsCal(false);
	}, []);

	const onConfirmCal = useCallback(
		(value: number) => {
			handleIsCal(false);
			onChangeStackValue(value / numOfCombinations);
		},
		[onChangeStackValue, numOfCombinations],
	);

	const scrollToBottom = useCallback(() => {
		if (allBets) {
			const couponContentRefHeight = couponContentRef.current?.getBoundingClientRect().height || 0;
			setTimeout(() => {
				couponContentRef.current?.scrollTo({ top: couponContentRefHeight + 300, behavior: 'smooth' });
			}, 100);
		}
	}, [allBets]);

	const calculateTotalOddsOfBlock = (block: PlaceBet[]) => {
		const totalOddsOfBlock = block.reduce((num: number, cur: PlaceBet) => num * cur.outcomeOdds, 1);
		return totalOddsOfBlock.toFixed(2);
	};

	const getSystemBetGroupName = (outcomeId: string) => {
		const numOfBankerBets = props.outcomes.filter((outcome) => outcome.banker).length;
		if (props.outcomes.find((outcome) => outcome.outcomeId === outcomeId)) {
			const index = props.outcomes.findIndex((outcome) => outcome.outcomeId === outcomeId && !outcome.banker);
			return systemBetGroupNameArray[index - numOfBankerBets + numOfBlocks];
		} else {
			const indexOfBlock = props.blocks.findIndex((block) => block.find((bet) => bet.outcomeId === outcomeId));
			return systemBetGroupNameArray[indexOfBlock];
		}
	};

	const getNumOfGroups = (outcomeId: string) => {
		if (props.outcomes.find((outcome) => outcome.outcomeId === outcomeId)) {
			return numOfSlipsWithGroups;
		} else {
			return numOfSlipsWithGroups + 1;
		}
	};

	useEffect(() => {
		scrollToBottom();
	}, [scrollToBottom]);

	const renderSingleSlip = (item: PlaceBet, index: number) => (
		<BetslipCouponCard
			key={`betslipCouponCard--allBets-${index}`}
			selectedOddsArray={item.betBuilderOddsArray ? item.betBuilderOddsArray : []}
			totalOdds={item.outcomeOdds}
			removeBet={props.removeBet}
			couponType="system"
			numOfItemsInBetslip={numOfSlipsWithGroups}
			isBetSlip
			isBetBuilder={item.isBetBuilder}
			handelSelectedBet={props.handelSelectedBet}
			completedEvents={props.completedEvents}
			bet={item}
			warnings={props.warnings}
			changeGroup={props.handleGroupChange}
			handleIsRestoringBets={props.handleIsRestoringBets}
			getSystemBetGroupName={getSystemBetGroupName}
			betActionErrors={props.betActionErrors}
			getNumOfGroups={getNumOfGroups}
		/>
	);

	const renderChangeCombination = () => (
		<ChangeSystemCombination
			selectedCombination={props.selectedCombination}
			handleSelectedCombination={(e: React.FormEvent<HTMLInputElement>) => props.handleSelectedCombination(e.currentTarget.value)}
			numOfSlipsWithGroups={numOfSlipsWithGroups}
			bankerCount={bankerCount}
		/>
	);

	return (
		<>
			{isCal && (
				<BetCalculator
					setStakeMethod={setStakeMethod}
					price={stakeValue}
					onCancelCal={onCancelCal}
					onConfirmCal={onConfirmCal}
					betType={props.betType}
					combination={numOfCombinations}
					bets={props.bets}
				/>
			)}
			<div ref={couponContentRef} className={`couponList--system flex flex-col gap-2 ${isCal ? 'overflow--hidden' : ''}`}>
				{bankerBets.map((bet, index) => (
					<div key={`bet-${index}`} className="System__groupedSlips">
						<div className="System__slipHeader System__slipHeader--sureBetTxt">{t('sureBet')}</div>
						{renderSingleSlip(bet, index)}
					</div>
				))}
				{props.blocks?.map((block, index) => (
					<div key={`block-${index}`} className="System__groupedSlips">
						<div className="System__slipHeader System__slipHeader--slipGroupTxt">
							<span>
								{t('group')} {systemBetGroupNameArray[index]}
							</span>
							<span>{calculateTotalOddsOfBlock(block)}</span>
						</div>
						{block.map(renderSingleSlip)}
					</div>
				))}
				{notBankerBets.map((bet, index) => (
					<div key={`bet-${index}`} className="System__groupedSlips">
						<div className="System__slipHeader System__slipHeader--slipGroupTxt">
							{t('group')} {systemBetGroupNameArray[index + numOfBlocks]}
						</div>
						{renderSingleSlip(bet, index)}
					</div>
				))}

				{!props.isDesktop && renderChangeCombination()}

				{!props.betLoading && (
					<ErrorCodes
						errorCodes={props.errorCode}
						betStatics={props.betStatics}
						onCloseError={props.onCloseError}
						scrollToBottom={scrollToBottom}
						betActionErrors={props.betActionErrors}
					/>
				)}
			</div>
		</>
	);
};

export default SystemBet;
